import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Monitor, Stethoscope, Activity, User,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';

/* ═══ Re-import BUDGET_DATA (same as BudgetSalute) ═══ */
const BUDGET_DATA = [
  {
    id: 'PAZ-8821', complessita: 'Alta', erogatore: 'Coop. Salute Roma',
    budgetPrevisto: 12400, spesaAttuale: 11200, distretto: 'Distretto 1', durataGG: 142, dataInizio: '02/11/2025',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Medicazione avanzata', qta: 48, costoUnit: 85, totale: 4080 },
      { desc: 'Accesso Infermieristico - Parametri vitali', qta: 36, costoUnit: 45, totale: 1620 },
      { desc: 'Fisioterapia motoria domiciliare', qta: 24, costoUnit: 95, totale: 2280 },
      { desc: 'Visita Medico Specialista (Geriatra)', qta: 6, costoUnit: 120, totale: 720 },
      { desc: 'OSS - Igiene e mobilizzazione', qta: 60, costoUnit: 35, totale: 2100 },
    ],
    assets: [
      { desc: 'Noleggio Kit Saturimetro + ECG', canone: '€ 45/mese', totale: 225 },
      { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 150 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 5700 },
      { name: 'Fisioterapia', value: 2280 },
      { name: 'OSS', value: 2100 },
      { name: 'Specialistica', value: 720 },
      { name: 'Telemedicina', value: 375 },
    ],
  },
  {
    id: 'PAZ-4410', complessita: 'Media', erogatore: 'Consorzio ADI Lazio',
    budgetPrevisto: 8200, spesaAttuale: 5740, distretto: 'Distretto 3', durataGG: 98, dataInizio: '10/01/2026',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Terapia EV', qta: 28, costoUnit: 75, totale: 2100 },
      { desc: 'Accesso Infermieristico - Prelievo', qta: 14, costoUnit: 45, totale: 630 },
      { desc: 'Fisioterapia respiratoria', qta: 16, costoUnit: 95, totale: 1520 },
      { desc: 'OSS - Assistenza pasti e igiene', qta: 30, costoUnit: 35, totale: 1050 },
      { desc: 'Educazione terapeutica caregiver', qta: 4, costoUnit: 60, totale: 240 },
    ],
    assets: [{ desc: 'Noleggio Concentratore O2 portatile', canone: '€ 80/mese', totale: 280 }],
    costiChart: [
      { name: 'Infermieristica', value: 2730 }, { name: 'Fisioterapia', value: 1520 },
      { name: 'OSS', value: 1050 }, { name: 'Educazione', value: 240 }, { name: 'Telemedicina', value: 280 },
    ],
  },
  {
    id: 'PAZ-6677', complessita: 'Alta', erogatore: 'Fondazione Gemelli@Home',
    budgetPrevisto: 15000, spesaAttuale: 16800, distretto: 'Distretto 2', durataGG: 186, dataInizio: '18/09/2025',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Gestione CVC', qta: 90, costoUnit: 95, totale: 8550 },
      { desc: 'Nutrizione Parenterale domiciliare', qta: 62, costoUnit: 45, totale: 2790 },
      { desc: 'Fisioterapia neuromuscolare', qta: 36, costoUnit: 110, totale: 3960 },
      { desc: 'Visita Palliativista', qta: 8, costoUnit: 130, totale: 1040 },
    ],
    assets: [
      { desc: 'Pompa infusione nutrizionale', canone: '€ 120/mese', totale: 720 },
      { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 180 },
      { desc: 'Materasso antidecubito dinamico', canone: '€ 55/mese', totale: 330 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 8550 }, { name: 'Nutrizione', value: 2790 },
      { name: 'Fisioterapia', value: 3960 }, { name: 'Specialistica', value: 1040 }, { name: 'Asset/Telemedicina', value: 1230 },
    ],
  },
  { id: 'PAZ-3301', complessita: 'Bassa', erogatore: 'Coop. Riabilita Roma', budgetPrevisto: 4800, spesaAttuale: 2160, distretto: 'Distretto 1', durataGG: 56, dataInizio: '22/02/2026', prestazioni: [{ desc: 'Fisioterapia motoria post-chirurgica', qta: 16, costoUnit: 95, totale: 1520 }, { desc: 'Accesso Infermieristico - Ferita chirurgica', qta: 8, costoUnit: 65, totale: 520 }, { desc: 'Visita Fisiatra di controllo', qta: 2, costoUnit: 110, totale: 220 }], assets: [], costiChart: [{ name: 'Fisioterapia', value: 1520 }, { name: 'Infermieristica', value: 520 }, { name: 'Specialistica', value: 220 }] },
  { id: 'PAZ-5590', complessita: 'Media', erogatore: 'ASL Roma 2 — Diretto', budgetPrevisto: 7600, spesaAttuale: 6840, distretto: 'Distretto 4', durataGG: 124, dataInizio: '15/12/2025', prestazioni: [{ desc: 'Accesso Infermieristico - Insulinoterapia', qta: 60, costoUnit: 40, totale: 2400 }, { desc: 'Accesso Infermieristico - Medicazione piede', qta: 24, costoUnit: 85, totale: 2040 }, { desc: 'Educazione terapeutica diabetologo', qta: 6, costoUnit: 80, totale: 480 }, { desc: 'OSS - Igiene e mobilizzazione', qta: 40, costoUnit: 35, totale: 1400 }], assets: [{ desc: 'Kit Telemonitoraggio Glicemia', canone: '€ 35/mese', totale: 140 }, { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 120 }], costiChart: [{ name: 'Infermieristica', value: 4440 }, { name: 'Educazione', value: 480 }, { name: 'OSS', value: 1400 }, { name: 'Telemedicina', value: 260 }] },
  { id: 'PAZ-7712', complessita: 'Alta', erogatore: 'Coop. Salute Roma', budgetPrevisto: 18000, spesaAttuale: 17100, distretto: 'Distretto 2', durataGG: 210, dataInizio: '20/08/2025', prestazioni: [{ desc: 'Accesso Infermieristico - Gestione PEG', qta: 96, costoUnit: 75, totale: 7200 }, { desc: 'Accesso Infermieristico - Aspirazione tracheale', qta: 48, costoUnit: 85, totale: 4080 }, { desc: 'Fisioterapia respiratoria intensiva', qta: 30, costoUnit: 110, totale: 3300 }, { desc: 'Visita Pneumologo', qta: 4, costoUnit: 130, totale: 520 }, { desc: 'Visita Nutrizionista clinico', qta: 6, costoUnit: 100, totale: 600 }], assets: [{ desc: 'Ventilatore domiciliare BiPAP', canone: '€ 180/mese', totale: 1260 }, { desc: 'Aspiratore tracheale portatile', canone: '€ 40/mese', totale: 280 }, { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 210 }], costiChart: [{ name: 'Infermieristica', value: 11280 }, { name: 'Fisioterapia', value: 3300 }, { name: 'Specialistica', value: 1120 }, { name: 'Asset/Telemedicina', value: 1750 }] },
  { id: 'PAZ-2204', complessita: 'Bassa', erogatore: 'Consorzio ADI Lazio', budgetPrevisto: 3200, spesaAttuale: 3450, distretto: 'Distretto 3', durataGG: 72, dataInizio: '05/02/2026', prestazioni: [{ desc: 'Fisioterapia domiciliare', qta: 20, costoUnit: 95, totale: 1900 }, { desc: 'Accesso Infermieristico - Parametri', qta: 12, costoUnit: 45, totale: 540 }, { desc: 'OSS - Supporto deambulazione', qta: 20, costoUnit: 35, totale: 700 }, { desc: 'Visita Fisiatra', qta: 3, costoUnit: 110, totale: 330 }], assets: [], costiChart: [{ name: 'Fisioterapia', value: 1900 }, { name: 'Infermieristica', value: 540 }, { name: 'OSS', value: 700 }, { name: 'Specialistica', value: 330 }] },
];

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6'];
const COMP_STYLE = {
  Alta: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Media: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Bassa: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
};

/* ═══ GANTT DATA GENERATOR ═══ */
function generateGanttData(pai) {
  const professionisti = ['Infermiere', 'Fisioterapista', 'OSS', 'Specialista'];
  const costiMap = { Infermiere: [45, 85], Fisioterapista: [95, 110], OSS: [35, 35], Specialista: [100, 130] };
  const stati = ['erogata', 'erogata', 'erogata', 'pianificata', 'pianificata', 'extra'];
  const settimane = 8;
  const rows = professionisti.map(prof => {
    const costi = costiMap[prof];
    const giorni = [];
    for (let w = 0; w < settimane; w++) {
      const numAccessi = prof === 'Specialista' ? (w % 3 === 0 ? 1 : 0) : prof === 'OSS' ? 3 : 2;
      for (let a = 0; a < numAccessi; a++) {
        const day = w * 7 + a * (prof === 'OSS' ? 2 : 3) + 1;
        if (day > settimane * 7) break;
        const costo = costi[Math.floor(Math.random() * costi.length)];
        const stato = w < 4 ? 'erogata' : w < 6 ? 'pianificata' : (Math.random() > 0.7 ? 'extra' : 'pianificata');
        giorni.push({ giorno: day, costo, stato, desc: `Visita ${prof}` });
      }
    }
    return { professionista: prof, giorni };
  });
  return { rows, totaleDays: settimane * 7 };
}

function PieTooltipCustom({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-0.5">{d.name}</p>
      <p className="text-slate-500">€ {d.value?.toLocaleString()}</p>
    </div>
  );
}

/* ═══ GANTT TOOLTIP ═══ */
function GanttTooltip({ item, prof, style }) {
  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-slate-800 dark:bg-slate-700 text-white text-[10px] whitespace-nowrap shadow-xl z-30 pointer-events-none" style={style}>
      <p className="font-bold">{prof} — {item.desc}</p>
      <p>Data: Giorno {item.giorno}</p>
      <p>Stato: {item.stato === 'erogata' ? 'Erogata/Pagata' : item.stato === 'pianificata' ? 'Pianificata (stimato)' : 'Extra budget'}</p>
      <p className="font-bold mt-0.5">Costo: € {item.costo.toFixed(2)}</p>
    </div>
  );
}

export default function ASLAuditDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hoveredDot, setHoveredDot] = useState(null);

  const pai = useMemo(() => BUDGET_DATA.find(p => p.id === id), [id]);
  const gantt = useMemo(() => pai ? generateGanttData(pai) : null, [pai]);

  if (!pai) {
    return (
      <AppLayout role="asl">
        <div className="p-6 text-center"><p className="text-muted-foreground">PAI non trovato.</p>
          <Button variant="outline" onClick={() => navigate('/asl/budget-salute')} className="mt-4">Torna alla lista</Button>
        </div>
      </AppLayout>
    );
  }

  const pct = Math.round((pai.spesaAttuale / pai.budgetPrevisto) * 100);
  const residuo = pai.budgetPrevisto - pai.spesaAttuale;
  const isOver = residuo < 0;

  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="audit-dettaglio-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <button onClick={() => navigate('/asl/budget-salute')} className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors group mb-2" data-testid="back-btn">
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />Torna a Budget di Salute
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Audit Economico PAI: {pai.id}</h1>
              <Badge className={`${COMP_STYLE[pai.complessita]} border-0 text-[10px] font-bold`}>{pai.complessita}</Badge>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{pai.erogatore} &middot; {pai.distretto} &middot; Attivo da {pai.dataInizio} &middot; {pai.durataGG} giorni</p>
          </div>
        </div>

        {/* 3 KPI Cards */}
        <div className="grid grid-cols-3 gap-4" data-testid="audit-kpi">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 text-center">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Budget Assegnato</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-white mt-1">€ {pai.budgetPrevisto.toLocaleString()}</p>
          </div>
          <div className={`rounded-2xl border p-5 text-center ${isOver ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40' : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40'}`}>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Speso (Consuntivo)</p>
            <p className={`text-2xl font-bold mt-1 ${isOver ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>€ {pai.spesaAttuale.toLocaleString()}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{pct}% del budget</p>
          </div>
          <div className={`rounded-2xl border p-5 text-center ${isOver ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40' : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40'}`}>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Residuo</p>
            <p className={`text-2xl font-bold mt-1 ${isOver ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{isOver ? '-' : ''}€ {Math.abs(residuo).toLocaleString()}</p>
            <p className={`text-[10px] mt-0.5 ${isOver ? 'text-red-500' : 'text-emerald-500'}`}>{isOver ? 'Over-budget' : 'Disponibile'}</p>
          </div>
        </div>

        {/* PieChart + Table side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PieChart */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 h-full flex flex-col" data-testid="audit-piechart">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-3">Distribuzione Costi</h2>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pai.costiChart} cx="50%" cy="50%" innerRadius={90} outerRadius={130} paddingAngle={3} dataKey="value" stroke="none">
                    {pai.costiChart.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltipCustom />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 shrink-0">
              {pai.costiChart.map((c, i) => (
                <span key={c.name} className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                  {c.name} ({((c.value / pai.spesaAttuale) * 100).toFixed(0)}%)
                </span>
              ))}
            </div>
          </div>

          {/* Prestazioni Table */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="audit-prestazioni-table">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <h2 className="text-sm font-bold text-slate-800 dark:text-white">Prestazioni Erogate</h2>
            </div>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-12 text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50 dark:bg-slate-800 px-4 py-2">
                <span className="col-span-5">Descrizione</span>
                <span className="col-span-2 text-center">Qta</span>
                <span className="col-span-2 text-center">€ Unitario</span>
                <span className="col-span-3 text-right">Totale</span>
              </div>
              {pai.prestazioni.map((p, i) => (
                <div key={i} className="grid grid-cols-12 px-4 py-2.5 border-t border-slate-100 dark:border-slate-800/60 text-xs items-center" data-testid={`prestazione-row-${i}`}>
                  <span className="col-span-5 text-slate-700 dark:text-slate-200 font-medium pr-2">{p.desc}</span>
                  <span className="col-span-2 text-center text-slate-500">{p.qta}</span>
                  <span className="col-span-2 text-center text-slate-500">€ {p.costoUnit}</span>
                  <span className="col-span-3 text-right font-bold text-slate-800 dark:text-white">€ {p.totale.toLocaleString()}</span>
                </div>
              ))}
              <div className="grid grid-cols-12 px-4 py-2.5 border-t-2 border-slate-300 dark:border-slate-600 text-xs bg-slate-50 dark:bg-slate-800/50">
                <span className="col-span-9 font-bold text-slate-700 dark:text-slate-200">Totale Prestazioni</span>
                <span className="col-span-3 text-right font-bold text-slate-800 dark:text-white">€ {pai.prestazioni.reduce((s, p) => s + p.totale, 0).toLocaleString()}</span>
              </div>
            </div>
            {/* Assets */}
            {pai.assets.length > 0 && (
              <div className="border-t border-slate-200 dark:border-slate-700">
                <div className="px-4 py-2.5 flex items-center gap-2 bg-teal-50/50 dark:bg-teal-950/10">
                  <Monitor className="w-3.5 h-3.5 text-teal-600" />
                  <span className="text-[10px] font-bold text-teal-700 dark:text-teal-300 uppercase tracking-wider">Asset & Telemedicina</span>
                </div>
                {pai.assets.map((a, i) => (
                  <div key={i} className="px-4 py-2 border-t border-teal-100 dark:border-teal-800/20 flex items-center justify-between text-xs">
                    <div><p className="font-medium text-slate-700 dark:text-slate-200">{a.desc}</p><p className="text-[10px] text-teal-500">{a.canone}</p></div>
                    <p className="font-bold text-slate-800 dark:text-white">€ {a.totale.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ═══ GANTT FINANZIARIO ═══ */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="gantt-section">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-slate-800 dark:text-white">Cronoprogramma Spesa (Gantt)</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">Distribuzione temporale delle prestazioni e assorbimento budget giornaliero</p>
            </div>
            {/* Legenda */}
            <div className="flex items-center gap-4 text-[10px] text-slate-500" data-testid="gantt-legend">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500" />Erogata / Pagata</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-sky-400 border-2 border-dashed border-sky-300" />Pianificata (stimato)</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500" />Extra budget</span>
            </div>
          </div>

          {/* Timeline Header */}
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Week headers */}
              <div className="flex items-center border-b border-slate-200 dark:border-slate-700 pb-2 mb-3">
                <div className="w-32 shrink-0" />
                {Array.from({ length: 8 }).map((_, w) => (
                  <div key={w} className="flex-1 text-center">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Sett. {w + 1}</p>
                    <p className="text-[8px] text-slate-300">{w * 7 + 1}–{(w + 1) * 7}g</p>
                  </div>
                ))}
              </div>

              {/* Rows */}
              {gantt?.rows.map((row, ri) => (
                <div key={row.professionista} className="flex items-center py-3 border-b border-slate-100 dark:border-slate-800/40 last:border-b-0" data-testid={`gantt-row-${ri}`}>
                  <div className="w-32 shrink-0 pr-3">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{row.professionista}</p>
                    <p className="text-[9px] text-slate-400">{row.giorni.length} accessi</p>
                  </div>
                  <div className="flex-1 relative h-8">
                    {/* Background grid */}
                    <div className="absolute inset-0 flex">
                      {Array.from({ length: 8 }).map((_, w) => (
                        <div key={w} className={`flex-1 ${w % 2 === 0 ? 'bg-slate-50/50 dark:bg-slate-800/20' : ''}`} />
                      ))}
                    </div>
                    {/* Dots */}
                    {row.giorni.map((item, di) => {
                      const leftPct = ((item.giorno - 1) / gantt.totaleDays) * 100;
                      const isHovered = hoveredDot === `${ri}-${di}`;
                      return (
                        <div
                          key={di}
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                          style={{ left: `${leftPct}%` }}
                          onMouseEnter={() => setHoveredDot(`${ri}-${di}`)}
                          onMouseLeave={() => setHoveredDot(null)}
                        >
                          <div
                            className={`w-4 h-4 rounded-full cursor-pointer transition-transform ${isHovered ? 'scale-150' : ''} ${
                              item.stato === 'erogata' ? 'bg-emerald-500 shadow-emerald-500/30 shadow-sm' :
                              item.stato === 'pianificata' ? 'bg-sky-400 border-2 border-dashed border-sky-200' :
                              'bg-red-500 shadow-red-500/30 shadow-sm'
                            }`}
                            data-testid={`gantt-dot-${ri}-${di}`}
                          />
                          {isHovered && <GanttTooltip item={item} prof={row.professionista} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
